import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PROFILE_IMAGE } from '../mock';
import { PageHeader } from './Animation';
import { User, MapPin, Mail, FileText } from 'lucide-react';
import { api, fileUrl, fetchContact } from '../lib/api';
import { useCategories } from '../context/CategoriesContext';
import { useFitScale } from '../hooks/useFitScale';

// Laid out at a fixed design size, then scaled to fit the screen without scrolling.
const FULL = { width: 1040, photo: 320, gap: 40, heading: 48, body: 16, infoCols: 2 };
const WIDE = { width: 1500, photo: 230, gap: 40, heading: 44, body: 16, infoCols: 4 };
const COMPACT = { width: 580, photo: 190, gap: 22, heading: 32, body: 15, infoCols: 2 };

export default function About() {
  const [content, setContent] = useState(null);
  const [portfolio, setPortfolio] = useState('');
  const { categories } = useCategories();
  const input = String(categories.length + 1).padStart(2, '0');
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const { scale, compact, wide, ready } = useFitScale(outerRef, innerRef, { maxScale: 2, fill: 0.92 });
  const d = compact ? COMPACT : wide ? WIDE : FULL;

  useEffect(() => {
    let alive = true;
    api.get('/about').then(({ data }) => { if (alive) setContent(data); }).catch(() => {});
    fetchContact().then((c) => { if (alive) setPortfolio(c.portfolio_filename || ''); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div ref={outerRef} className="page-in relative w-full h-full overflow-hidden" data-testid="page-about">
      <div ref={innerRef} className="absolute left-1/2 top-1/2"
           style={{ width: d.width, transform: `translate(-50%, -50%) scale(${scale})`, visibility: ready ? 'visible' : 'hidden' }}>
        <PageHeader input={input} title="ABOUT THE STATION" icon={User} />
        {content
          ? <AboutBody content={content} d={d} portfolio={portfolio} />
          : <div className="font-osd text-[#8b6a3e] text-sm">// SIGNAL ACQUIRING…</div>}
      </div>
    </div>
  );
}

function AboutBody({ content, d, portfolio }) {
  const photoUrl = content.photo_filename ? fileUrl(content.photo_filename) : PROFILE_IMAGE;
  return (
    <div className="flex items-start" style={{ gap: d.gap }}>
      <div className="shrink-0 relative rounded-lg overflow-hidden border-2 border-[#3a2512] bg-[#0a0502]" style={{ width: d.photo }}>
        <img src={photoUrl} alt="Owen Dilley in the studio" className="w-full h-auto block aspect-[4/5] object-cover" />
        <div className="absolute top-2 left-2 font-osd text-[11px] text-[#f5a623] bg-[#1a0e05]/90 px-2 py-1 rounded border border-[#f5a623]/50">
          {content.intro_label || '// STUDIO FEED — LIVE'}
        </div>
      </div>

      <div className="flex-1 min-w-0 space-y-5">
        <h2 className="font-display text-[#ffe9b8] chroma leading-tight" style={{ fontSize: d.heading }}>{content.heading}</h2>
        <div className="font-body text-[#d8b887] leading-[1.7] space-y-3" style={{ fontSize: d.body }}>
          {(content.paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
        </div>

        {content.info_blocks?.length > 0 && (
          <div className="grid gap-3 pt-2" style={{ gridTemplateColumns: `repeat(${d.infoCols}, minmax(0, 1fr))` }}>
            {content.info_blocks.map((b, i) => (
              <InfoBlock key={i} label={b.label} icon={i === 0 ? MapPin : i === 1 ? Mail : undefined}>{b.value}</InfoBlock>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-4 pt-2">
          {content.cta_primary_text && (
            content.cta_primary_link?.startsWith('http') || content.cta_primary_link?.startsWith('mailto:')
              ? <a href={content.cta_primary_link} className="retro-btn" data-testid="about-cta-primary">{content.cta_primary_text}</a>
              : <Link to={content.cta_primary_link || '/animation'} className="retro-btn" data-testid="about-cta-primary">{content.cta_primary_text}</Link>
          )}
          {content.cta_secondary_text && (
            <a href={content.cta_secondary_link || 'mailto:owen@dilley.studio'} className="retro-btn retro-btn-ghost" data-testid="about-cta-secondary">
              {content.cta_secondary_text}
            </a>
          )}
          {portfolio && (
            <a href={fileUrl(portfolio)} target="_blank" rel="noopener noreferrer" className="retro-btn retro-btn-ghost inline-flex items-center gap-2" data-testid="about-cta-portfolio">
              <FileText className="w-4 h-4" /> RESUME (PDF)
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoBlock({ label, icon: Icon, children }) {
  return (
    <div className="border border-[#3a2512] rounded p-3 bg-[#1a0e05]/60">
      <div className="flex items-center gap-2 font-osd text-xs text-[#f5a623] tracking-widest mb-1">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="font-body text-sm text-[#d8b887] break-words">{children}</div>
    </div>
  );
}
